import React from "react";

import { flattenedChildren } from "@kikoff/client-utils/src/react";
import ContainerButton from "@kikoff/components/src/v1/buttons/ContainerButton";
import KLink from "@kikoff/components/src/v1/navigation/KLink";
import { PageControllerV1 } from "@kikoff/hooks/src/usePageController";
import { combineClasses } from "@kikoff/utils/src/string";

import PageAlign from "@component/dashboard/offer/page_align";
import PageViewLayout from "@component/dashboard/offer/page_view_layout";
import { nativeDispatchEvent } from "@util/mobile";

import { FlowLayoutProps } from "../hooks/useFlowController";
import { LayoutRegistration } from "../hooks/usePageFlow";

import styles from "./PersistentAppBar.module.scss";

interface PersistentAppBarProps
  // For backwards compat with flow controller
  extends Partial<LayoutRegistration>,
    Partial<FlowLayoutProps> {
  className?: string;
  title?: React.ReactNode;
  defaultControls?: boolean;
  hideFlowControls?: boolean;
  color?: React.CSSProperties["background"];
  noBack?: boolean;
  noClose?: boolean;
  // Enables back button on first view and attaches handler
  firstBack?(): void;
  onBack?(context: { pageController: PageControllerV1 }): void;
  onClose?: React.MouseEventHandler<HTMLAnchorElement>;
  exitRoute?: string;
  slim?: boolean;
  closeButton?: React.ReactNode;
}

const PersistentAppBar: React.FC<PersistentAppBarProps> = ({
  slim,
  layout = {} as LayoutRegistration["layout"],
  className,
  title,
  defaultControls,
  color,
  noBack,
  noClose,
  firstBack,
  onClose,
  viewIndex = layout.localPages?.index,
  pageController = layout.pageController,
  exitRoute = "/dashboard",
  onBack,
  children: _children,
  closeButton,
}) => {
  const children = flattenedChildren(_children);

  const displayBackButton =
    !noBack && defaultControls && (pageController.current > 0 || firstBack);

  return (
    <div
      className={combineClasses(
        styles["persistent-app-bar"],
        slim && styles["slim"],
        className
      )}
    >
      <PageAlign wrap style={color ? { background: color } : {}}>
        <div className={styles.container}>
          {displayBackButton && (
            <div>
              <ContainerButton
                onClick={() => {
                  if (onBack) return onBack({ pageController });
                  if (pageController.current === 0) firstBack?.();
                  else pageController.prev();
                }}
                className={styles.back}
              >
                
              </ContainerButton>
            </div>
          )}
          <div className={styles.title} data-cy="app-bar-title">
            {title}
          </div>
          {defaultControls && !noClose && (
            <div>
              {closeButton !== undefined ? (
                closeButton
              ) : (
                <KLink
                  variant="unstyled"
                  href={exitRoute}
                  onClick={nativeDispatchEvent("close", {
                    defaultHandler: onClose,
                  })}
                  className={styles.close}
                >
                  
                </KLink>
              )}
            </div>
          )}
        </div>
      </PageAlign>
      <div className={styles.body}>
        <PageViewLayout viewIndex={viewIndex} views={children} />
      </div>
    </div>
  );
};

export default PersistentAppBar;
